import { Injectable } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APIService, KPI } from '../../@core/api';

import { ErrorService } from './error.service';
import { HistoryService } from './history.service';
import { InvestmentCategoriesService } from './investment-categories.service';
import { InvestmentExecutionsService } from './investment-executions.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { IdMap, ScopeService } from './scope.service';

/**
 * Used to get and to format an investment history
 */
@Injectable()
export class InvestmentHistoryService extends HistoryService {

  private investmentPriorities: IdMap<KPI.InvestmentPriority>;
  private investmentStatuses: IdMap<KPI.InvestmentStatus>;
  private investmentTypes: IdMap<KPI.InvestmentType>;
  private investmentReasons: KPI.InvestmentReason[];
  private investmentExecutions: KPI.InvestmentExecution[];
  private investmentCategories: KPI.InvestmentCategory[];
  private categories: IdMap<KPI.Category>;
  private subCategories: IdMap<KPI.SubCategory>;

  constructor(
    protected errorService: ErrorService,
    protected translateService: TranslateService,
    private api: APIService,
    private investmentCategoriesService: InvestmentCategoriesService,
    private executionService: InvestmentExecutionsService,
    private investmentReasonsService: InvestmentReasonsService,
    private scopeService: ScopeService,
  ) {
    super(errorService, translateService);

    this.translationMap = {
      'investmentslice': _('investment.investmentslice.label'),
      'investmentsponsorship': _('investment.investmentsponsorship.label'),
      'sponsor': _('investment.sponsor'),
      'percentage': _('investment.percentage'),
      'id': _('investment.id'),
      'year': _('investment.year'),
      'status': _('investment.status'),
      'price': _('investment.price'),
      'label': _('investment.label'),
      'asset': _('investment.asset'),
      'scenario': _('investment.scenario'),
      'investment_type': _('investment.investment_type'),
      'priority': _('investment.priority'),
      'quantity': _('investment.quantity'),
      'budget_reference': _('investment.budget_reference'),
      'slices': _('investment.slices'),
      'billing_type': _('investment.billing_type'),
      'supplier': _('investment.supplier'),
      'sponsorships': _('investment.sponsorships'),
      'initial_schedule': _('investment.initial_schedule'),
      'initial_schedule_to': _('investment.initial_schedule_to'),
      'final_schedule': _('investment.final_schedule'),
      'final_schedule_to': _('investment.final_schedule_to'),
      'comments': _('investment.comments'),
      'local_id': _('investment.local_id'),
      'taxonomy_objective': _('investment.taxonomy_objective'),
      'taxonomy_category': _('investment.taxonomy_category'),
      'reasons': _('investment.reasons'),
      'nb_tasks': _('investment.nb_tasks'),
      'documents': _('investment.documents'),
      'sponsor_id': _('investment.sponsor_id'),
      'investment_id': _('investment.investment_id'),
      'status_id': _('investment.status_id'),
      'scenario_id': _('investment.scenario_id'),
      'asset_id': _('investment.asset_id'),
      'theoretical_schedule': _('investment.theoretical_schedule'),
      'current_price': _('investment.current_price'),
      'is_deleted': _('investment.is_deleted'),
      'use_budget_reference': _('investment.use_budget_reference'),
      'in_project': _('investment.in_project'),
      'is_capex': _('investment.is_capex'),
      'investment_type_id': _('investment.investment_type_id'),
      'suggested_lifetime': _('investment.suggested_lifetime'),
      'created_on': _('investment.created_on'),
      'energy_saving': _('investment.energy_saving'),
      'add_price_reason': _('investment.add_price_reason'),
      'additional_price': _('investment.additional_price'),
      'execution': _('investment.execution'),
      'duration_deviation': _('investment.duration_deviation'),
      'has_no_impact': _('investment.has_no_impact'),
      'remaining': _('investment.remaining'),
      'spent': _('investment.spent'),
      'investmentspendingslice': _('investment.investmentspendingslice'),
      'at_date': _('investment.at_date'),
      'is_extra_work': _('investment.is_extra_work'),
      'is_estimation': _('investment.is_estimation'),
      'budget_origin': _('investment.budget_origin'),
      'imported_at': _('investment.imported_at'),
      'import_source': _('investment.import_source'),
      'import_reference': _('investment.import_reference'),
      'import_statuses': _('investment.import_statuses'),
      'investment_category': _('investment.investment_category'),
      'reference': _('investment.reference'),
      'category': _('investment.category'),
      'sub_category': _('investment.sub_category'),
      'last_change': _('investment.last_change'),
      'qrcode': _('investment.qrcode'),
      'suggested_lifetime_year': _('investment.suggested_lifetime_year'),
      'first_audit_year': _('investment.first_audit_year'),
      'first_audit_note': _('investment.first_audit_note'),
      'barcode': _('investment.barcode'),
      'served_zone': _('investment.served_zone'),
      'maintenance_code': _('investment.maintenance_code'),
      'asset_type_id': _('investment.asset_type_id'),
      'zone_id': _('investment.zone_id'),
      'zone_details': _('investment.zone_details'),
      'ownership_type_id': _('investment.ownership_type_id'),
      'installation_year': _('investment.installation_year'),
      'has_invoicing': _('investment.has_invoicing'),
      'deviation_reason_id': _('investment.deviation_reason_id'),
      'price_modified_on': _('investment.price_modified_on'),
      'investment': _('investment.investment'),
    };

    this.undisplayedObject = [
      'investmentsponsorship',
    ];

    this.undisplayedFields = [
      'id',
      'local_id',
      'sponsor_id',
      'investment_id',
      'status_id',
      'scenario_id',
      'asset_id',
      'investment_type_id',
      'percentage',
      'billing_type_id',
      'is_deleted',
      'building_id',
      'execution_id',
      'category_id',
      'investment_category_id',
      'budget_origin_id',
      'sub_category_id',
    ];
  }

  /**
   * Load the history for an investment
   *
   * @param investment the investment that we wish to recover its history
   */
  public loadHistoryForInvestment(investmentId: number): Observable<KPI.History> {
    return combineLatest(
      this.api.investments_investment_history(investmentId),
      this.scopeService.investmentPriorities$,
      this.scopeService.investmentStatuses$,
      this.scopeService.investmentTypes$,
      this.investmentReasonsService.investmentReasons$,
      this.executionService.investmentExecutions$,
      this.investmentCategoriesService.investmentCategories$,
      this.scopeService.categories$,
      this.scopeService.subCategories$,
    ).pipe(
      map(([
        history,
        investmentPriorities,
        investmentStatuses,
        investmentTypes,
        investmentReasons,
        investmentExecutions,
        investmentCategories,
        categories,
        subCategories,
      ]) => {
        this.investmentPriorities = investmentPriorities;
        this.investmentStatuses = investmentStatuses;
        this.investmentTypes = investmentTypes;
        this.investmentReasons = investmentReasons;
        this.investmentExecutions = investmentExecutions;
        this.investmentCategories = investmentCategories;
        this.categories = categories;
        this.subCategories = subCategories;
        return this.cleanHistory(history);
      },
      ),
    );
  }


  /**
   * If the change is not clear for the user we recover some information that we will display
   *
   * @param change
   */
  protected manageSpecialField(change: KPI.ChangeItem): KPI.ChangeItem {
    const newChange: KPI.ChangeItem = change;
    switch (change.field_name) {
      case 'priority':
        newChange.new = this.getName(this.investmentPriorities[change.new.toString()]);
        if (change.old) {
          newChange.old = this.getName(this.investmentPriorities[change.old.toString()]);
        }
        break;

      case 'status_id':
        newChange.field_name = 'status';
        newChange.new = this.getName(this.investmentStatuses[change.new.toString()]);
        if (change.old) {
          newChange.old = this.getName(this.investmentStatuses[change.old.toString()]);
        }
        break;

      case 'investment_category_id':
        newChange.field_name = 'investment_category';
        if (change.new) {
          newChange.new = this.getName(this.investmentCategories[change.new.toString()]);
        }
        if (change.old) {
          newChange.old = this.getName(this.investmentCategories[change.old.toString()]);
        }
        break;

      case 'category_id':
        newChange.field_name = 'category';
        if (change.new) {
          newChange.new = this.getName(this.categories[change.new.toString()]);
        }
        if (change.old) {
          newChange.old = this.getName(this.categories[change.old.toString()]);
        }
        break;

      case 'sub_category_id':
        newChange.field_name = 'sub_category';
        if (change.new) {
          newChange.new = this.getName(this.subCategories[change.new.toString()]);
        }
        if (change.old) {
          newChange.old = this.getName(this.subCategories[change.old.toString()]);
        }
        break;

      case 'investment_type_id':
        newChange.field_name = 'investment_type';
        newChange.new = this.getName(this.investmentTypes[change.new.toString()]);
        if (change.old) {
          newChange.old = this.getName(this.investmentTypes[change.old.toString()]);
        }
        break;

      case 'execution_id':
        newChange.field_name = 'execution';
        newChange.new = this.getName(this.investmentExecutions[change.new.toString()]);
        if (change.old) {
          newChange.old = this.getName(this.investmentExecutions[change.old.toString()]);
        }
        break;

      case 'reasons':
        newChange.new = this.reasonsId2ReasonsString(change.new);
        if (change.old) {
          newChange.old = this.reasonsId2ReasonsString(change.old);
        }
        break;

      default:
        if (typeof newChange.new === 'boolean') {
          newChange.new = this.boolean2String(<boolean>change.new);
        }
        if (typeof newChange.old === 'boolean') {
          newChange.old = this.boolean2String(<boolean>change.old);
        }
        break;
    }

    return newChange;
  }

  /**
   * Transform a list of reason id to a string
   */
  private reasonsId2ReasonsString(reasonsId): string {
    const reasonsString: string[] = [];
    for (const reasonId of reasonsId) {
      const reason = this.investmentReasons.find(
        (reas) => reas.id === parseInt(reasonId, 10),
      );
      reasonsString.push(reason.name);
    }

    return reasonsString.join(', ');
  }

  public getInvestmentYearAndStatusHistory(investmentId: number): Observable<KPI.YearAndStatusHistoryItem[]> {
    return this.loadHistoryForInvestment(investmentId).pipe(
      map((hist) => {
        const history: KPI.YearAndStatusHistoryItem[] = [];
        let lastYearChange: number;
        let lastStatusChange: string;
        hist.history_items.map((item) => {

          // Find the year and status changes
          const yearChange = item.changes.find((change) =>
            change.field_name === 'investment.year' && change.object_type === 'investment.investmentslice.label'
          )?.new;
          const statusChange = item.changes.find((change) =>
            change.field_name === 'investment.status' && change.object_type === 'investment.investmentslice.label'
          )?.new;

          // If the year or status has changed, add a new entry to the history
          if (
            (yearChange && yearChange !== lastYearChange)
            || (statusChange && statusChange !== lastStatusChange)
          ) {

            // Store the last change for the next iteration
            if (yearChange) {
              lastYearChange = <number>yearChange;
            }
            if (statusChange) {
              lastStatusChange = <string>statusChange;
            }

            history.push({
              year: lastYearChange,
              status: lastStatusChange,
              date: item.date,
            });
          }
        });
        return history;
      }),
    );
  }
}
